/**
 * AppointmentPageV2.js — IOO-324 Simplify Date/Time Selection
 *
 * Identical to AppointmentPage.js EXCEPT:
 *  - Imports ClinicBookingV2  instead of ClinicBooking
 *  - Imports PreferredTimeV2  instead of PreferredTime
 *
 * All callbacks, state, and step routing are unchanged.
 * This file is the integration point; swap back to original imports to revert.
 */
import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { isClinicHiddenFromMainWidget } from "../../utils/clinicUtils";
import { Config } from "../Common/Config";
import "./AppointmentPage.css";
import {
  ChevronRight,
  ChevronDown,
  Phone,
  MapPin,
  Menu,
  Search,
} from "lucide-react";
import { Tooltip, Badge, Modal } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  universalDummyData,
  mockAppointmentData,
  mockHealthScreenData,
} from "./data";

import LoadingSpinner from "../Common/LoadingSpinner";
import axios from "axios";
import UnifiedAppointmentType from "./UnifiedAppointmentType.js";
import { pushTraceLogEvent } from "./StepComponents/sessionEventPush.js";
import useBookingSession from "../../hooks/useBookingSession";
import SessionWarningModal from "../Shared/SessionWarningModal";
import WalletPaymentPendingModal from "../Shared/WalletPaymentPendingModal";
import BookingProgressBar from "./BookingProgressBarV2"; // IOO-336 Version B — animated bar (no circles)

// Perf: these are only rendered behind a currentStep/flag check, never on first
// paint (currentStep starts at 1, which renders UnifiedAppointmentType) — load
// them on demand instead of shipping them in the initial bundle.
const BookingJourney = lazy(() => import("./index.js"));
const BookingForm = lazy(() => import("../Dashboard/BookingForm"));
// IOO-324 V2 components
const ClinicBooking = lazy(() => import("../Dashboard/ClinicBookingV2"));
const PreferredTime = lazy(() => import("../Dashboard/PreferredTimeV2"));
const AppointmentSelection = lazy(() => import("../Dashboard/AppointmentSelection"));
// import Step4 from "./StepComponents/Step4";
const Step4 = lazy(() => import('./StepComponents/Step4'));


const AppointmentPageV2 = (props) => {
  // console.log("AppointmentPage props:", props); // Debugging line
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // we commeted 1step so it will start from two
  // Suppresses AppointmentPage's own progress bar when d2c/health Hbooking sub-flow is active
  const [isInD2CSubFlow, setIsInD2CSubFlow] = useState(false);
  //3rd step type specification
  const [clinicBookingType, setClinicBookingType] = useState("");
  const [showAppointmentSelection, setShowAppointmentSelection] =
    useState(false);
  const [gpDuration, setGpDuration] = useState(false);
  const [gpLang, setGpLang] = useState(false);
  const [allClinics, setAllClinics] = useState({});
  const [clinicData, setClinicData] = useState({});

  const [combineAllClinicAndClinicById, setCombineAllClinicAndClinicById] =
    useState([]);

  const [showAppoinmentData, setShowAppoinmentData] = useState({});
  const [getAppointmentDownPart, setGetAppointmentData] = useState({});
  const [dataExists, setDataExists] = useState(false);

  const [callbackData, setCallbackData] = useState();
  // appointmentSelection page
  const [showAppoinmentDataDisplay, setshowAppoinmentDataDisplay] =
    useState(undefined);

  // 5-minute countdown state
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);

  // §5.5 — tracks the active wallet payment method when WalletPaymentPendingModal is open.
  // Set by Step4 via onWalletPaymentTypeChange; null when no wallet payment is in progress.
  // Passed to extendSession so the backend can bypass the max-extensions cap.
  const walletPaymentTypeRef = useRef(null);
  // Ref populated by Step4 — lets the parent-level WalletPaymentPendingModal
  // trigger Step4's full handleWalletPaymentTimeout cleanup when its countdown fires.
  const walletTimeoutCallbackRef = useRef(null);

  // Suppress timer display and SessionWarningModal while Apple/Google Pay native sheet is active.
  // Reactivated on payment failure so the user can see remaining time.
  const [walletPaymentActive, setWalletPaymentActive] = useState(false);

  // IOO-275 — WalletPaymentPendingModal state managed here (parent level)
  // so Step4 local re-renders (loading, isProcessing) cannot close the modal.
  const [walletModalState, setWalletModalState] = useState({
    open: false,
    method: 'gpay',     // 'gpay' | 'apple'
    amount: 0,
    confirming: false,
  });
  const [step4SubStep, setStep4SubStep] = useState(1);
  const openWalletModal = (method, amount) => setWalletModalState(s => ({ ...s, open: true, method, amount }));
  const closeWalletModal = () => setWalletModalState(s => ({ ...s, open: false, confirming: false }));
  const setWalletConfirming = (v) => setWalletModalState(s => ({ ...s, confirming: v }));
  const handleSessionExpired = () => {
    const slot_Session = localStorage.getItem("bookingSessionId");
    pushTraceLogEvent({
      event: "Session Expired",
      payload: {
        step: "user session expired",
        date: new Date().toISOString().split("T")[0],
        slot_session_id: slot_Session
      },
      step_status: "success",
    });
    setShowTimeExpiredModal(true);
  };
  const {
    timeRemaining,
    countdownStartTime,
    showWarningModal,
    maxExtensionsReached,
    startSession,
    stopSession,
    extendSession,
    declineExtension,
    extensionsUsed,
    settings,
    suppressWarning,
    resumeWarning,
    isExtending,
    extendError,
    resumeSession,
  } = useBookingSession(handleSessionExpired);
  const [locSearchLog, setlocSearchLog] = useState([]);

  // for Booking form we need the duration api and language api call

  // Fetch and transform the API response
  const fetchGpDuration = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URLOPEN}appointment-duration`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            category: "clinician_type",
          },
        },
      );

      // need format like this
      // Transform the array to an object with duration_id as key

      const formattedData = response.data.data.reduce((acc, item) => {
        acc[item.duration_id] = item;
        return acc;
      }, {});

      const durationObjectById = response.data.data.reduce((acc, item) => {
        acc[item.duration_id] = {
          duration: item.duration,
          // content: item.content
        };
        return acc;
      }, {});

      // setGpDuration(response.data);
      // setDurationBYid(durationObjectById)
      setGpDuration(formattedData);
      // console.log("Formatted Duration gp data:", formattedData);
    } catch (error) {
      console.error("Error fetching health screen data:", error);
    }
  };
  const fetchGpLanguage = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URLOPEN}languages`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            category: "clinician_type",
          },
        },
      );

      // need format like this
      // Transform the array to desired format
      const formattedData = response.data.data.map((lang) => ({
        label: lang.language_name,
        value: lang.language_id,
      }));

      // setGpLang(response.data);
      setGpLang(formattedData);
      // console.log("Formatted health screen data:", formattedData);
    } catch (error) {
      console.error("Error fetching health screen data:", error);
    }
  };
  const fetchAllClinic = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URLOPEN}clinics`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          params: {
            category: "clinician_type",
          },
        },
      );

      // need format like this
      // Transform the array to desired format
      // const formattedData = response.data.data.map(lang => ({
      //   label: lang.language_name,
      //   value: lang.language_id
      // }));

      const rawClinics = response.data?.data || [];
      const allClinicsMap = rawClinics.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});

      let filteredClinics = rawClinics;
      if (!props?.partnerId) {
        filteredClinics = rawClinics.filter(
          (item) => !isClinicHiddenFromMainWidget(item, props?.partnerId, allClinicsMap)
        );
      }

      setAllClinics({
        ...response.data,
        data: filteredClinics,
      });
      setCombineAllClinicAndClinicById(allClinicsMap);
      // setGpLang(formattedData);
      // console.log("Formatted health screen data:", formattedData);
    } catch (error) {
      console.error("Error fetching all clinic data:", error);
    }
  };
  const fetchClinicData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URLOPEN}clinic/${props.clinicId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      // console.log("response:::",response?.data?.data)
      setClinicData(response?.data?.data);
    } catch (error) {
      console.error("Error fetching all clinic data:", error);
    }
  };

  // ── DF-01: Only fetch data strictly needed for Step 1 on mount ───────────
  // fetchGpDuration → deferred to Step 2 (BookingForm)
  // fetchAllClinic  → deferred to Step 3 (ClinicBooking)
  useEffect(() => {
    if (props.clinicId) fetchClinicData();
    fetchGpLanguage(); // needed early: passed to Hbooking sub-flow in UnifiedAppointmentType
  }, []);

  // ── Prefetch the lazy step chunks once the critical first paint is done ──
  // The steps below are lazy() so they're excluded from the initial bundle,
  // but if the user reaches one before its chunk (JS + CSS) has finished
  // loading, it can render with its own stylesheet not yet applied — e.g.
  // the step-2 button row briefly rendering without `.bottomButtons`' flex
  // gap, making the two buttons look like they overlap. Warming the chunks
  // here (after idle, so it never competes with first paint) means they're
  // already resolved from cache by the time the user steps forward.
  useEffect(() => {
    const prefetchStepChunks = () => {
      import("./index.js");
      import("../Dashboard/BookingForm");
      import("../Dashboard/ClinicBookingV2");
      import("../Dashboard/PreferredTimeV2");
      import("../Dashboard/AppointmentSelection");
      import("./StepComponents/Step4");
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(prefetchStepChunks);
    } else {
      setTimeout(prefetchStepChunks, 1500);
    }
  }, []);

  // ── Restore Klarna failed redirect state on mount ─────────────────────────
  useEffect(() => {
    try {
      const isFailed = sessionStorage.getItem("klarna_payment_failed") === "true" ||
        localStorage.getItem("klarna_payment_failed") === "true";
      if (isFailed) {
        const raw = sessionStorage.getItem("klarnaBookingContext") ||
          localStorage.getItem("klarnaBookingContext_bk");
        if (raw) {
          const savedCtx = JSON.parse(raw);
          if (savedCtx) {
            // Restore step 4 states in the nested structure expected by the component
            if (savedCtx.showAppoinmentData) {
              setShowAppoinmentData({
                showAppoinmentData: savedCtx.showAppoinmentData,
                data: savedCtx.data || {}
              });
            }
            if (savedCtx.callbackData) {
              setCallbackData(savedCtx.callbackData);
            }
            if (savedCtx.clinicBookingType) {
              setClinicBookingType(savedCtx.clinicBookingType);
            }
            if (savedCtx.showAppointmentSelection) {
              setShowAppointmentSelection(savedCtx.showAppointmentSelection);
            }
            setDataExists(true);
            setCurrentStep(4); // Land on Step 4 (Payment)

            // Resume countdown timer
            if (savedCtx.countdownEndTime) {
              const remaining = savedCtx.countdownEndTime - Date.now();
              if (remaining > 0) {
                resumeSession(Date.now(), remaining, savedCtx.extensionsUsed || 0);
              }
            }
          }
        }
      }
    } catch (e) {
      console.error("Failed to restore Klarna recovery state in AppointmentPage:", e);
    }
  }, [resumeSession]);

  // ── DF-01: Lazy-load Step 2 data ─────────────────────────────────────────
  useEffect(() => {
    if ((currentStep === 2 || currentStep === 4) && !gpDuration) {
      fetchGpDuration();
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── DF-01: Lazy-load Step 3 data ─────────────────────────────────────────
  useEffect(() => {
    if ((currentStep === 3 || currentStep === 4) && Object.keys(allClinics).length === 0) {
      fetchAllClinic();
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // console.log("___ClinicAPIData___",{ allClinics, combineAllClinicAndClinicById, gpDuration, gpLang  })

  // Countdown timer is now handled by useBookingSession hook

  useEffect(() => {
    setShowAppoinmentData((prev) => {
      // Check if we already have a custom category ID restored (like 2 or 3)
      const existingCatId = prev?.showAppoinmentData?.clinic?.productCategory?.product_category_id;
      const isRestored = existingCatId && existingCatId !== 1;

      return {
        ...prev,
        showAppoinmentData: {
          ...(prev?.showAppoinmentData || {}),
          clinic: {
            ...(prev?.showAppoinmentData?.clinic || {}),
            productCategory: {
              title: isRestored
                ? (prev?.showAppoinmentData?.clinic?.productCategory?.title || "Health Screen")
                : "GP appointment",
              product_category_id: isRestored ? existingCatId : 1,
              ...(isRestored && {
                product_id: prev?.showAppoinmentData?.clinic?.productCategory?.product_id
              })
            },
          },
        },
        data: {
          ...(prev?.data || {}),
          clinic: {
            id: null,
            name: "",
            latitude: clinicData?.latitude,
            longitude: clinicData?.longitude,
            address: "",
            clinic_website_description: "",
            position: "",
          },
          partnerId: props?.partnerId,
          allClinicData: allClinics,
          allClinicDataByID: combineAllClinicAndClinicById,
          isProtected: false,
        },
      };
    });
  }, [allClinics, combineAllClinicAndClinicById]);

  const handleContinue = () => {
    setCurrentStep((prev) => prev + 1);
  };

  //! step 1 dummy data start [getAllHealthScreenData]

  const callbackAppointment = (data, formType) => {
    // console.log("Callback triggered with:", data, formType);
    setCallbackData(data);

    // Start dynamic countdown
    startSession();

    handleContinue();
  };

  const callBackForbackpage = () => {
    // console.log("Back button clicked");
  };

  //! step 1 dummy data end

  //! step 2 dummy data start [mockAppointmentData, mockHealthScreenData, mockData ]

  const mockCallbackBookingForm = (formData, type) => {
    // console.log("Booking form submitted:", { formData, type });

    if (!formData) {
      return;
    }

    // Handle language - works whether formData.language is the value or the full object
    let selectedLanguage;
    if (typeof formData.language === "object") {
      // If language comes as {label, value} object
      selectedLanguage = {
        label: formData.language.label,
        value: formData.language.value,
      };
    } else {
      // If language comes as just the value (string or number)
      const langObj = gpLang.find((lang) => String(lang.value) === String(formData.language));
      selectedLanguage = {
        label: langObj?.label || "English", // fallback to English
        value: formData.language,
      };
    }

    // Format the appointment data with the form selections
    const formattedAppointmentData = {
      showAppoinmentData: {
        clinic: {
          productCategory: {
            title: "GP appointment",
            product_category_id: 1,
          },
        },
        gender: formData.gender, // Add gender from form
        language: selectedLanguage?.value,
        selectedLanguage,
        duration: formData.duration, // Add duration from form
        type: type, // Add booking type
      },
      data: {
        clinic: {
          id: null,
          name: "C",
          latitude: clinicData?.latitude,
          longitude: clinicData?.longitude,
          address: "",
          clinic_website_description: "",
          position: "",
        },
        durationObjectById: gpDuration, // Use the gpDuration data
        languageData: gpLang, // Language lookup for AppointmentInfo display
        allClinicData: allClinics,
        allClinicDataByID: combineAllClinicAndClinicById,
        isProtected: false,
      },
    };

    setShowAppoinmentData(formattedAppointmentData);
    setClinicBookingType(type);
    setCurrentStep(3);
  };

  // console.log("getAppointmentDownPart", getAppointmentDownPart)

  const mockCallBackForBackPage = (source) => {
    // console.log("Back to:", source);
    setCurrentStep(1);
    // navigate('/book');
  };

  //! step 2 dummy data end

  //! step 3 dummy data start [universalDummyData]

  // Modified callback for ClinicBooking
  const callbackClinicBooking = (data, type) => {
    setDataExists(false);
    // console.log("ok_callbackClinicBooking data:", data, type);

    if (!data) return;

    // Prefer the already-resolved selectedLanguage from step 2 to avoid re-lookup mismatches
    let selectedLanguage = showAppoinmentData.showAppoinmentData?.selectedLanguage;
    if (!selectedLanguage) {
      const langId = showAppoinmentData.showAppoinmentData?.language;
      if (langId) {
        // Use String comparison to handle numeric API values vs string state values
        const langObj = gpLang && gpLang.find((lang) => String(lang.value) === String(langId));
        selectedLanguage = {
          label: langObj?.label || "English", // fallback to English
          value: langObj?.value || langId,
        };
      }
    }

    const appointmentPayload = {
      showAppoinmentData: {
        clinic: showAppoinmentData.showAppoinmentData.clinic,
        duration: showAppoinmentData.showAppoinmentData.duration,
        gender: showAppoinmentData.showAppoinmentData.gender,
        language: selectedLanguage?.value,
        selectedLanguage: selectedLanguage, // carry forward for label display
        type: type,
        selectedClinics: data.selectedClinics,
        selectedSlots: data.selectedSlots,
      },
      data: {
        isProtected: false,
        allClinicDataByID: combineAllClinicAndClinicById,
        date: new Date().toISOString().split("T")[0], // Current date
      },
    };
    if (data.selectedSlots.length > 0) {
      setGetAppointmentData(appointmentPayload);

      // AppointmentSelection (the old re-pick-the-same-slot screen) is
      // skipped for this journey now — ClinicBookingV2/PreferredTimeV2
      // already blocked the slot via block-slot before calling this
      // callback, so build the callbackData shape Step4 needs directly
      // (mirrors what AppointmentSelection's handleContinueButton used to
      // hand to callbackAppointment) and advance straight to Step 4.
      const clinicId = data.selectedClinics[0];
      const slotInfo = data.selectedSlots[0];
      const clinicRecord = combineAllClinicAndClinicById[clinicId] || {};
      const builtCallbackData = {
        clinicData: {
          id: Number(clinicId),
          name: clinicRecord.name ?? clinicRecord.clinic_name ?? '',
          address: clinicRecord.address ?? clinicRecord.clinic_address ?? '',
          latitude: clinicRecord.latitude,
          longitude: clinicRecord.longitude,
        },
        info: {
          price: String(slotInfo.info.price),
          start_time: slotInfo.info.start_time,
          end_time: slotInfo.info.end_time,
          date_select: slotInfo.date,
          additional_charges: slotInfo.info.additional_charges,
          is_higher_price: slotInfo.info.is_higher_price,
          special_pricing_band: slotInfo.info.special_pricing_band,
          normal_price: slotInfo.info.normal_price,
        },
        date: slotInfo.date,
        slot: `${clinicId}${slotInfo.date}${slotInfo.info.start_time}`,
      };
      setCallbackData(builtCallbackData);
      startSession();
      setCurrentStep(currentStep + 1);
    }
  };

  const callBackForbackpageClinicBooking = (page) => {
    setGetAppointmentData({});
    setCurrentStep(currentStep - 1);
  };

  // Mock callback functions
  const callbackPreferredTime = (data, type) => {
    // console.log("Preferred time callback:", data, type);
    if (data?.selectedSlots.length > 0) {
      // enable only when we select slots
      setShowAppointmentSelection(true); // Enable AppointmentSelectionHardcoded
    }
  };

  const callBackForbackpagePreferredTime = (page) => { };

  //! New callback for AppointmentSelection

  //! step 3 dummy data end

  //! step 4 appointment Data
  const callbackAppointmentSelection = (selectedSlot, type) => {
    setShowAppointmentSelection(false);
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    // console.log(text)
    setGetAppointmentData({});
    setCurrentStep(currentStep - 1);
  };

  const handleContinueStep4 = () => {
    setCurrentStep(5); // Booking confirmed — progress bar shows 100%
  };

  // Stop countdown timer function
  const stopCountdown = () => {
    stopSession();
  };

  const handleTimeExpiredOk = () => {
    setShowTimeExpiredModal(false);
    stopSession();
    try {
      sessionStorage.removeItem("klarnaBookingContext");
      localStorage.removeItem("klarnaBookingContext_bk");
      sessionStorage.removeItem("klarna_payment_failed");
      localStorage.removeItem("klarna_payment_failed");
    } catch (_) { }

    const isPatientMode = process.env.REACT_APP_PATIENT_MODE === 'true';
    if (!isPatientMode) {
      const pToken = localStorage.getItem('patient_auth_token');
      if (pToken) {
        fetch(`${Config.urlOpen}patient/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${pToken}`,
            'Content-Type': 'application/json',
          },
        })
          .catch(err => console.error("Logout API error:", err))
          .finally(() => {
            localStorage.removeItem('patient_auth_token');
            localStorage.removeItem('patient_account_info');
          });
      }
    }

    const isLoggedIn = !!localStorage.getItem("token");
    if (isLoggedIn) {
      window.location.href = process.env.REACT_APP_PATIENT_MODE === 'true' ? "/patient/appointments" : "/";
      return;
    }
    setCurrentStep(1);
    setCallbackData(null);
    setShowAppoinmentData({});
    setGetAppointmentData({});
    setClinicBookingType("");
    setShowAppointmentSelection(false);
    setDataExists(false);
  };

  let today = new Date();
  let year = today.getFullYear();

  // Countdown display component
  const CountdownDisplay = () => {
    // console.log("CountdownDisplay debug:", { countdownStartTime, timeRemaining, currentStep, walletPaymentActive });
    // Hide timer entirely while Apple/Google Pay native sheet is active
    if (walletPaymentActive) return null;

    if (
      !countdownStartTime ||
      timeRemaining === 0 ||
      (currentStep !== 3 && currentStep !== 4)
    ) {
      return null;
    }

    const minutes = Math.floor(timeRemaining / (1000 * 60));
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
    const timeString = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

    // Calculate expiration time for tooltip
    const expiresAt = new Date(Date.now() + timeRemaining);
    const expiresLocal12 = dayjs(expiresAt).format("hh:mm A");

    return (
      <div style={{ zIndex: 10 }}>
        <Tooltip title={`Expires at ${expiresLocal12}`}>
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            <Badge
              count={timeString}
              color="#008385"
              style={{ fontSize: 14, marginRight: 6 }}
            />
            <ClockCircleOutlined style={{ fontSize: 20, color: "#008385" }} />
          </div>
        </Tooltip>
      </div>
    );
  };

  const bookingDetails = {
    showAppoinmentData: {
      ...showAppoinmentData,
      ...showAppoinmentData.showAppoinmentData,
      price: callbackData?.info.price || "0.00", // Add price from callbackData
      special_pricing_band: callbackData?.info?.special_pricing_band,
      normal_price: callbackData?.info?.normal_price,
    },
    callbackData,
    // date: callbackData?.date,
    date: callbackData?.info?.date_select,
    time: callbackData?.info.start_time,
    isProtected: false,
    data: {
      clinic: {
        id: 40,
        name: "City Medical Center",
        latitude: clinicData?.latitude,
        longitude: clinicData?.longitude,
        address: "123 Medical St, London",
        clinic_website_description: "<p>Modern medical facility</p>",
        position: "Main Building",
      },
      durationObjectById: gpDuration,
      languageData: gpLang, // Real language data for label resolution
      allClinicData: allClinics,
      allClinicDataByID: combineAllClinicAndClinicById,
      isProtected: false,
    },

    // "11:45",
  };

  return (
    <div className="page-wrapper">
      <div className="top-bar" style={{ display: "none" }}>
        <div className="top-bar-inner">
          <div className="top-links">
            <span>For Patients</span>
            <span>For Business Customers</span>
            <span>Become a Member</span>
          </div>
        </div>
      </div>

      <main className="main">
        {process.env.REACT_APP_SHOW_HEADER === "true" && (
          <>
            {currentStep == 0 ? (
              <>
                <div className="app-container">
                  {/* Top Bar */}
                  <div className="top-bar">
                    <div className="top-bar-inner">
                      <div className="top-links">
                        <span>For Patients</span>
                        <span>For Business Customers</span>
                        <span>Become a Member</span>
                      </div>
                    </div>
                  </div>
                  <header className="main-header">
                    <div className="header-inner">
                      <div className="header-rowBooking">
                        {/* Logo */}
                        <div className="logo">
                          <img
                            src={
                              "https://pbldcbe.webdezign.uk/static/media/ldc_logo_new.1f2a7d791d5f16307994.png"
                            }
                            alt="Logo"
                            style={{ height: "40px", aspectRatio: "4096 / 544", width: "auto" }}
                          />
                        </div>

                        {/* Contact */}
                        <div className="contact-info">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "end",
                            }}
                          >
                            <ChevronRight size={12} />
                            <span>Book online</span>
                          </div>
                          <div className="contact-call">
                            <ChevronRight size={12} />
                            <span>Or call 020 3993 0813</span>
                          </div>
                        </div>

                        {/* Mobile Menu Icon */}
                        <Menu className="mobile-menu-icon" />
                      </div>
                    </div>

                    {/* Navigation */}
                    <nav className="navigation">
                      <div className="nav-inner">
                        <div className="nav-links">
                          <Search className="icon-small" />
                          <span className="nav-item">
                            Services <ChevronDown className="icon-small" />
                          </span>
                          <span className="nav-item">
                            Blood Tests <ChevronDown className="icon-small" />
                          </span>
                          <span className="nav-item">
                            STI Tests <ChevronDown className="icon-small" />
                          </span>
                          <span className="nav-item">Health screens</span>
                          <span className="nav-item">Prices</span>
                          <span className="nav-item">
                            Locations <ChevronDown className="icon-small" />
                          </span>
                          <span className="nav-item">Online Doctor</span>
                          <span className="nav-item">About</span>
                        </div>
                      </div>
                    </nav>

                    {/* Search Section */}
                    <div className="search-section">
                      <div className="search-inner ">
                        <span className="search-title">
                          Book at your nearest clinic
                        </span>
                        <div className="search-form">
                          <input type="text" placeholder="Enter a location" />
                        </div>

                        <button
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <span className="location-icon">
                            <MapPin style={{ color: "#008385" }} size={12} />
                          </span>{" "}
                          Use your current location
                        </button>
                      </div>
                    </div>
                  </header>
                </div>
              </>
            ) : (
              <header className="main-header">
                <div className="header-inner">
                  <div className="header-rowBooking">
                    {/* Logo */}
                    <div className="logo">
                      <img
                        src={
                          "https://pbldcbe.webdezign.uk/static/media/ldc_logo_new.1f2a7d791d5f16307994.png"
                        }
                        alt="Logo"
                        style={{ height: "40px", aspectRatio: "4096 / 544", width: "auto" }}
                      />
                    </div>

                    {/* Contact */}
                    <div className="contact-info">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "end",
                        }}
                      >
                        <ChevronRight size={12} />
                        <span>Book online</span>
                      </div>
                      <div className="contact-call">
                        <ChevronRight size={12} />
                        <span>Or call 020 3993 0813</span>
                      </div>
                    </div>

                    {/* Mobile Menu Icon */}
                    <Menu className="mobile-menu-icon" />
                  </div>
                </div>
              </header>
            )}
          </>
        )}

        {/* IOO-336 — Progress Bar: 5 visual steps.
            currentStep 1 → visual step 1 (Appointment Type)
            currentStep 2 → visual step 2 (Your Preferences)
            currentStep 3, showAppointmentSelection=false → visual step 3 (Select Clinic)
            currentStep 3, showAppointmentSelection=true  → visual step 4 (Select Slot)
            currentStep 4 → visual step 5 (Payment) */}
        {currentStep >= 1 && currentStep <= 5 && !isInD2CSubFlow && (
          <BookingProgressBar
            currentStep={currentStep}
            showAppointmentSelection={showAppointmentSelection}
            step4SubStep={step4SubStep}
            clinicBookingType={clinicBookingType}
          />
        )}

        {currentStep === 0 && (
          <Suspense fallback={<LoadingSpinner />}>
            <BookingJourney onNext={() => setCurrentStep(1)} />
          </Suspense>
        )}
        {currentStep === 1 && (
          <UnifiedAppointmentType
            onNext={() => setCurrentStep(2)}
            onBack={() => setCurrentStep(0)}
            onSubFlowEnter={() => setIsInD2CSubFlow(true)}
            onSubFlowExit={() => setIsInD2CSubFlow(false)}
            languageData={gpLang}
            productCategoryId={props.productCategoryId}
            productId={props.productId}
            clinicId={props.clinicId}
            hashCode={props.hashCode}
            partnerId={props.partnerId}
            clinicData={clinicData}
            hideAdditionalPreferences={props.hideAdditionalPreferences}
          />
        )}
        {currentStep === 2 && (
          <Suspense fallback={<LoadingSpinner />}>
            <BookingForm
              showAppoinmentData={mockAppointmentData}
              getAllHealthScreenData={mockHealthScreenData}
              // data={mockData}
              data={{ durationObjectById: gpDuration, languageData: gpLang }}
              callbackBookingForm={mockCallbackBookingForm}
              callBackForbackpage={mockCallBackForBackPage}
              hideAdditionalPreferences={props.hideAdditionalPreferences}
            />
          </Suspense>
        )}
        {currentStep === 3 && (
          <>
            <div style={{ position: "relative" }}>
              {/* <CountdownDisplay /> */}
              {/* // PreferredTimeclinic or  PreferredTime */}
              {clinicBookingType === "PreferredTimeclinic" && (
                // For ClinicBooking
                <Suspense fallback={<LoadingSpinner />}>
                  <ClinicBooking
                    check={dataExists}
                    CountdownDisplay={CountdownDisplay}
                    stopCountdown={stopCountdown}
                    clinicId={props.clinicId}
                    showAppoinmentData={showAppoinmentData.showAppoinmentData}
                    prefanceData={showAppoinmentData.showAppoinmentData}
                    data={{ ...showAppoinmentData.data, languageData: gpLang || [] }}
                    metaData={getAppointmentDownPart?.showAppoinmentData}
                    callback={callbackClinicBooking}
                    callBackForbackpage={callBackForbackpageClinicBooking}
                    getAllHealthScreenData={
                      universalDummyData.getAllHealthScreenData
                    }
                    locSearchLog={locSearchLog}
                    setlocSearchLog={setlocSearchLog}
                  />
                </Suspense>
              )}

              {clinicBookingType === "PreferredTime" && (
                // For PreferredTime
                <Suspense fallback={<LoadingSpinner />}>
                  {/* <h1>PreferredTime</h1> */}
                  <PreferredTime
                    check={dataExists}
                    clinicId={props.clinicId}
                    CountdownDisplay={CountdownDisplay}
                    stopCountdown={stopCountdown}
                    goback={() => setCurrentStep(currentStep - 1)}
                    showAppoinmentData={showAppoinmentData.showAppoinmentData}
                    prefanceData={showAppoinmentData.showAppoinmentData}
                    metaData={getAppointmentDownPart?.showAppoinmentData}
                    data={{ ...showAppoinmentData.data, languageData: gpLang || [] }}
                    callback={callbackClinicBooking}
                    callBackForbackpage={callBackForbackpagePreferredTime}
                    getAllHealthScreenData={
                      universalDummyData.getAllHealthScreenData
                    }
                  />
                </Suspense>
              )}

              {showAppointmentSelection && (
                <Suspense fallback={<LoadingSpinner />}>
                  <AppointmentSelection
                    showAppoinmentData={getAppointmentDownPart.showAppoinmentData}
                    data={getAppointmentDownPart.data}
                    callback={callbackAppointment}
                    callBackForbackpage={() => {
                      setShowAppointmentSelection(false);
                      setDataExists(true);
                      setGetAppointmentData({});
                    }}
                    locSearchLog={locSearchLog}
                  />
                </Suspense>
              )}
            </div>
          </>
        )}

        {(currentStep === 4 || currentStep === 5) && (
          <div style={{ position: "relative" }}>
            {/* <CountdownDisplay /> */}
            <Suspense fallback={<LoadingSpinner />}>
              <Step4
                CountdownDisplay={CountdownDisplay}
                stopCountdown={stopCountdown}
                onBack={handleBack}
                // onBack={ok}
                onContinue={handleContinueStep4}
                bookingDetails={bookingDetails}
                onStepChange={setStep4SubStep}
                extensionDurationSeconds={settings.extension_duration_seconds ?? 300}
                // §5.5 — Step4 calls this when WalletPaymentPendingModal opens/closes
                // so SessionWarningModal can forward the payment_type bypass to extendSession.
                onWalletPaymentTypeChange={(paymentType) => {
                  walletPaymentTypeRef.current = paymentType;
                }}
                // Suppress timer + SessionWarningModal while wallet native sheet is active
                onWalletPaymentStart={(paymentType) => {
                  walletPaymentTypeRef.current = paymentType;
                  setWalletPaymentActive(true);
                  suppressWarning(); // stop interval from firing warning modal + trace log
                  return extendSession(paymentType); // extend slot immediately (IOO-275 §5.5 bypass)
                }}
                onWalletPaymentEnd={() => {
                  walletPaymentTypeRef.current = null;
                  setWalletPaymentActive(false);
                  resumeWarning(); // re-enable warning modal + trace log
                }}
                // IOO-275 — wallet modal lifecycle (state lives in parent to survive Step4 re-renders)
                onWalletModalOpen={openWalletModal}
                onWalletModalClose={closeWalletModal}
                onWalletConfirming={setWalletConfirming}
                walletTimeoutCallbackRef={walletTimeoutCallbackRef}
                timeRemaining={timeRemaining}
                extensionsUsed={extensionsUsed}
              />
            </Suspense>
          </div>
        )}

        {/* Session Warning Modal */}
        <SessionWarningModal
          isOpen={showWarningModal && !walletPaymentActive}
          timeRemaining={timeRemaining}
          // §5.5 — forward the active wallet payment_type so the backend bypass rule fires
          // when a wallet payment is in progress. Non-wallet flows pass null → no change.
          onExtend={() => extendSession(walletPaymentTypeRef.current ?? null)}
          onDecline={declineExtension}
          maxExtensionsReached={maxExtensionsReached}
          extensionsUsed={extensionsUsed}
          maxExtensions={settings.max_extensions}
          isExtending={isExtending}
          extendError={extendError}
        />

        {/* IOO-275 — Wallet Payment Pending Modal (parent-level to survive Step4 re-renders) */}
        <WalletPaymentPendingModal
          isOpen={walletModalState.open}
          paymentMethod={walletModalState.method}
          paymentTimeoutMs={(settings.extension_duration_seconds ?? 300) * 1000}
          totalAmount={walletModalState.amount}
          bookingDetails={bookingDetails}
          isConfirming={walletModalState.confirming}
          onPaymentTimeout={() => {
            // Delegate to Step4's handleWalletPaymentTimeout so it can close
            // both the parent modal and its own local modal in one pass.
            if (walletTimeoutCallbackRef.current) {
              walletTimeoutCallbackRef.current();
            } else {
              closeWalletModal(); // fallback
            }
          }}
        />

        {/* Time Expired Modal */}
        <Modal
          title="Session Expired"
          open={showTimeExpiredModal && !walletPaymentActive}
          onOk={handleTimeExpiredOk}
          onCancel={handleTimeExpiredOk}
          closable={false}
          centered
          okText="OK"
          cancelButtonProps={{ style: { display: "none" } }}
          maskClosable={false}
        >
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: "16px", marginBottom: "10px" }}>
              Your booking session has expired.
            </div>
            <div style={{ fontSize: "14px", color: "#666" }}>
              Please start the booking process again.
            </div>
          </div>
        </Modal>
      </main>

      {process.env.REACT_APP_SHOW_HEADER === "true" && (
        <>
          <footer className="footer">
            <div
              className="logo"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <img
                src={
                  "https://pbldcbe.webdezign.uk/static/media/ldc_logo_new.1f2a7d791d5f16307994.png"
                }
                alt="Logo"
                style={{ height: "40px", aspectRatio: "4096 / 544", width: "auto" }}
              />
            </div>
          </footer>
        </>
      )}

      <style jsx>
        {`
          .app-container {
            // font-family:  'Lato', sans-serif;
            background-color: white;
          }

          .top-bar {
            background-color: #4b5563;
            color: white;
            padding: 0.5rem 1rem;
            padding-left: 0px;
            font-size: 0.9rem;
          }

          .top-bar-inner {
            margin-left: 10px;
            display: flex;
            justify-content: start;
          }

          .top-links span {
            margin-right: 1.5rem;
            cursor: pointer;
          }

          .main-header {
            background-color: #008385;
            color: white;
          }

          .header-inner {
            margin: auto;
            padding: 1rem;
          }

          .header-rowBooking {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .logo {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .logo-title {
            font-size: 1.25rem;
            font-weight: bold;
          }

          .logo-subtitle {
            font-size: 0.85rem;
            opacity: 0.9;
          }

          .contact-info {
            text-align: right;
            display: none;
          }

          .contact-call {
            display: flex;
            justify-content: end;
            align-items: center;
            gap: 0.3rem;
          }

          .mobile-menu-icon {
            display: block;
          }

          @media (min-width: 768px) {
            .contact-info {
              display: block;
            }
            .mobile-menu-icon {
              display: none;
            }
          }

          .navigation {
            border-top: 0.05px solid #d3eced;
            background-color: #008385;
            padding: 0.5rem 1rem;
          }
          @media (max-width: 768px) {
            .navigation {
              display: none;
            }
          }

          .nav-links {
            display: flex;
            gap: 1rem;
            overflow-x: auto;
            white-space: nowrap;
            font-size: 0.9rem;
            align-items: center;
          }

          .nav-item {
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }

          .search-section {
            background: #f2fafa;
            color: #333;
            padding: 0.4rem;
          }

          .search-inner {
            margin: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1rem;
          }

          @media (min-width: 768px) {
            .search-inner {
              flex-direction: row;
              justify-content: center;
              align-items: center;
            }
          }

          .search-form {
            display: flex;
            gap: 0.5rem;
          }

          .search-form input {
            padding: 0.5rem;
            border: 1px solid #ccc;
            border-radius: 5px;
          }

          .search-form button {
            padding: 0.5rem;
            border: 1px solid #ccc;
            background: #f0f0f0;
            color: #0d9488;
            border-radius: 5px;
            cursor: pointer;
          }

          button {
            border: 0;
            outline: 0;
            background: transparent;
          }

          .purple-banner {
            background-color: #5d0e93;
            color: white;
            text-align: center;
            padding: 0.4rem;
            font-weight: bold;
          }

          .hero-section {
            background-color: #008385;
            z-index: -1;
          }

          .hero-inner {
            position: relative;
            margin: auto;
            background-image: url("https://pbldcbe.webdezign.uk/static/media/doctor-patient.31b3df054c73cf0734d1.jpg"); /* Replace with your actual image path doctor image */
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            height: 52vh;
          }

          .hero-text {
            position: absolute;
            top: 100px;
            left: 100px;
            max-width: fit-content;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(4px);
            text-align: left;
          }

          .hero-text h2 {
            margin: 0;
            padding-right: 2rem;
            font-size: 2.5rem;
            font-weight: 50;
          }

          .hero-text button {
            background: #14b8a6;
            color: white;
            padding: 1rem 2rem;
            border: none;
            border-radius: 9999px;
            font-size: 1rem;
            cursor: pointer;
          }

          .selectButton {
            background: #008385;
            color: white;
            padding: 1rem 2rem;
            border: none;
            border-radius: 9999px;
            font-size: 1rem;
            cursor: pointer;
          }

          .hero-image {
            flex: 1;
          }

          .hero-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .main-features-section {
            position: relative;
            z-index: 2;
            margin-top: -3rem;
            background: #fff;
            margin-inline: 0.8rem;
          }

          .features-section {
            background: #f9fafb;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2rem;
            padding: 4rem 1rem;
          }

          @media (min-width: 768px) {
            .features-section {
              flex-direction: row;
              justify-content: center;
            }
          }

          .feature {
            background: white;
            padding: 2rem;
            border-radius: 8px;
            text-align: center;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          }

          .feature h3 {
            color: #0d9488;
            margin-top: 1rem;
          }

          .featured-section {
            color: #008385;
            text-align: center;
            padding: 2rem 1rem;
          }
        `}
      </style>
    </div>
  );
};

export default AppointmentPageV2;
